import { StoreExt } from '@utils/reactExt'
import { action, observable, runInAction } from 'mobx'
import _ from 'lodash'
import { Schema$CompanyPick, Schema$CompanyPickManager, Schema$CompanyPickNotice } from '@interface/company-pick'
import { authStore, globalStore, matchupStore } from '@store/index'
import { COMPANY_PICK_TYPE, EXCEPTIONAL_UNIQUE_ID, USER_COMMENT_TYPE } from '@v2/constants/common'
import * as Utils from '@utils/utils'
import StorageUtils from '@utils/storage-utils'
import { Schema$UserComment, Schema$UserCommentExt } from '@interface/comment'
import { SPORTS_TYPE } from '@interface/common'

export class CompanyPickStore extends StoreExt {
  @observable
  homeCompanyPicks: Schema$CompanyPick[]

  // 분석가 픽 리스트
  @observable
  companyPicks: Schema$CompanyPick[]

  // 선택한 분석가 픽
  @observable
  selectedCompanyPick: Schema$CompanyPick

  // 홈화면 분석가 리스트
  @observable
  homeCompanyPickManagers: Schema$CompanyPickManager[]

  // 분석가 리스트
  @observable
  companyPickManagers: Schema$CompanyPickManager[]

  // 선택한 분석가 픽의 최근 코멘트
  @observable
  selectedCompanyPickRecentComments: Schema$UserComment[]

  // 채팅영역에 표시하는 공지 멘트
  @observable
  notice: Schema$CompanyPickNotice

  // 선택한 분석가
  selectedCompanyPickManager: Schema$CompanyPickManager

  @observable
  isMatchupPageCall: boolean

  isCompanyPickLikeLoading: boolean

  isFavoriteMatchLoading: boolean

  @observable
  isNewNotice: boolean

  fetchCompanyPick = async (companyPickId: string): Promise<void> => {
    try {
      const response = await this.api.companyPick.fetchCompanyPick({ id: companyPickId })
      this.setSelectedCompanyPick(response.item)
    } catch (err) {
      console.error(err)
    }
  }

  fetchHomeCompanyPicks = async (params: {
    type?: COMPANY_PICK_TYPE,
    managerUserPromiseId?: string
  }): Promise<void> => {
    try {
      const response = await this.api.companyPick.fetchHomeCompanyPicks(params)
      this.setHomeCompanyPicks(response.homeCompanyPicks)
      this.setHomeCompanyPickManagers(response.homeCompanyPickManagers)
    } catch (err) {
      console.error(err)
    }
  }

  fetchCompanyPickManagers = async (params: {
    userPromiseIds?: string[]
  }): Promise<void> => {
    try {
      const response = await this.api.companyPick.fetchManagers(params)
      this.setCompanyPickManagers(response.managers)
    } catch (err) {
      console.error(err)
    }
  }

  fetchRecentComments = async (): Promise<void> => {
    try {
      const params = {
        commentType: USER_COMMENT_TYPE.COMPANY_PICK_COMMENT,
        targetId: this.selectedCompanyPick.id,
      }
      const result = await this.api.comment.fetchRecentComments(params)
      this.setSelectedCompanyPickRecentComments(result.items)
    } catch (err) {
      console.error(err)
    }
  }

  fetchCompanyPickContent = async (): Promise<void> => {
    try {
      const params = {
        companyPickId: this.selectedCompanyPick.id,
      }
      const result = await this.api.companyPick.fetchCompanyPickContent(params)
      runInAction(() => {
        this.selectedCompanyPick.content = result?.item?.content ?? ''
      })
    } catch (err) {
      console.error(err)
    }
  }

  fetchNotice = async (): Promise<void> => {
    try {
      const result = await this.api.companyPick.fetchNotice(null)
      this.setNotice(result.notice)
      this.updateIsNewNotice()
    } catch (err) {
      console.error(err)
    }
  }

  createCommentCallback = (comment: Schema$UserComment): boolean => {
    try {
      const { targetId } = comment
      this.unshiftAndDropRightSelectedCompanyPickRecentComments(comment)

      let managerUserPromiseId: string

      let result = this.increaseHomeCompanyPickCommentCount(targetId)
      managerUserPromiseId = result?.userPromiseId || managerUserPromiseId

      result = this.increaseCompanyPickCommentCount(targetId)
      managerUserPromiseId = result?.userPromiseId || managerUserPromiseId

      result = this.increaseNoticeCompanyPickCommentCount(targetId)
      managerUserPromiseId = result?.userPromiseId || managerUserPromiseId

      this.increaseManagerCommentCount(managerUserPromiseId)
    } catch (err) {
      console.error(err)
      return false
    }
    return true
  }

  updateCommentCallback = (comment: Schema$UserComment): boolean => {
    try {
      // 수정한 항목이 최신항목에 포함되면 내용을 바꿔야 함
      // 최신 댓글 갱신
      // this.unshiftAndDropRightSelectedCompanyPickRecentComments(result.comment)
      this.replaceSelectedCompanyPickRecentComments(comment)
    } catch (err) {
      console.error(err)
      return false
    }
    return true
  }

  deleteCommentCallback = async (comment: Schema$UserComment): Promise<boolean> => {
    try {
      // 최신 댓글 갱신
      this.fetchRecentComments().catch(err => console.error(err))

      // 코멘트 카운트 갱신
      let managerUserPromiseId: string
      let result: Schema$CompanyPick
      result = this.decreaseCompanyPickCommentCount(comment.targetId)
      managerUserPromiseId = result?.userPromiseId || managerUserPromiseId

      result = this.decreaseNoticeCompanyPickCommentCount(comment.targetId)
      managerUserPromiseId = result?.userPromiseId || managerUserPromiseId

      this.decreaseManagerCommentCount(managerUserPromiseId)
    } catch (err) {
      console.error(err)
      return false
    }

    return true
  }

  createCompanyPickFirstReadLog = async (companyPick: Schema$CompanyPick): Promise<void> => {
    if (_.isEmpty(companyPick)) {
      return
    }

    const { id } = companyPick
    try {
      await this.api.companyPick.createCompanyPickFirstReadLog({ companyPickId: id })
      this.updateAllCompanyPicksAlreadyReadInfo(id, true)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setHomeCompanyPicks = (picks: Schema$CompanyPick[]): void => {
    this.homeCompanyPicks = picks
  }

  @action
  setSelectedCompanyPick = (pick: Schema$CompanyPick): void => {
    this.selectedCompanyPick = pick
  }

  @action
  setHomeCompanyPickManagers = (managers: Schema$CompanyPickManager[]): void => {
    this.homeCompanyPickManagers = managers
  }

  @action
  setCompanyPickManagers = (managers: Schema$CompanyPickManager[]): void => {
    this.companyPickManagers = managers
  }

  @action
  setSelectedCompanyPickRecentComments = (comments: Schema$UserComment[]): void => {
    const commentExt: Schema$UserCommentExt[] = []
    for (const item of comments) {
      commentExt.push({ ...item, visibleEditor: false })
    }
    this.selectedCompanyPickRecentComments = commentExt
  }

  @action
  setCompanyPicks = (companyPicks: Schema$CompanyPick[]): void => {
    this.companyPicks = companyPicks
  }

  setSelectedCompanyPickManager = (manager: Schema$CompanyPickManager): void => {
    this.selectedCompanyPickManager = manager
  }

  /**
   * 최근 댓글 목록 처음에 새로운 댓글을 추가하고 마지막 댓글은 제거한다. (일정양만 표시해야 하기 때문)
   * @param comment
   */
  @action
  unshiftAndDropRightSelectedCompanyPickRecentComments = (comment: Schema$UserComment) => {
    if (comment.targetId !== this.selectedCompanyPick.id) {
      return
    }
    if (comment.commentDepth > 0) {
      return
    }

    if (_.size(this.selectedCompanyPickRecentComments) < 3) {
      this.selectedCompanyPickRecentComments.unshift(comment)
      return
    }

    this.selectedCompanyPickRecentComments = _.chain(this.selectedCompanyPickRecentComments)
      .dropRight().unshift(comment).value()
  }

  /**
   * 최근 댓글 목록 중 댓글 내용을 갱신한다.
   *
   * @param commentParam
   */
  @action
  replaceSelectedCompanyPickRecentComments = (commentParam: Schema$UserComment) => {
    if (commentParam.targetId !== this.selectedCompanyPick.id) {
      return
    }

    const newComments = []
    _.forEach(this.selectedCompanyPickRecentComments, comment => {
      if (comment.id === commentParam.id) {
        newComments.push(commentParam)
      } else {
        newComments.push(comment)
      }
    })

    this.selectedCompanyPickRecentComments = newComments
  }

  /**
   * 홈 화면 프리미엄 분석가 픽의 댓글 수를 증가 한다.
   *
   * @param companyPickId
   */
  @action
  increaseHomeCompanyPickCommentCount = (companyPickId: string): Schema$CompanyPick => {
    let found
    _.forEach(this.homeCompanyPicks, pick => {
      if (pick.id === companyPickId) {
        pick.commentCount += 1
        found = pick
      }
    })
    return found
  }

  /**
   * 프리미엄 분석가 분석글 목록 중 프리미엄 분석가 분석글의 댓글 수를 증가 한다.
   *
   * @param companyPickId
   */
  @action
  increaseCompanyPickCommentCount = (companyPickId: string): Schema$CompanyPick => {
    let found
    _.forEach(this.companyPicks, pick => {
      if (pick.id === companyPickId) {
        pick.commentCount += 1
        found = pick
      }
    })
    this.setCompanyPicks(this.companyPicks)
    return found
  }

  @action
  increaseNoticeCompanyPickCommentCount = (companyPickId: string): Schema$CompanyPick => {
    let found

    if (_.isEmpty(this.notice)) {
      return found
    }

    const { companyPicks } = this.notice
    _.forEach(companyPicks, pick => {
      if (pick.id === companyPickId) {
        pick.commentCount += 1
        found = pick
      }
    })
    return found
  }

  @action
  increaseManagerCommentCount = (managerUserPromiseId: string): void => {
    const found = _.find(this.companyPickManagers, manager => manager.userPromiseId === managerUserPromiseId)
    if (found) {
      found.commentCount += 1
    }
  }

  @action
  decreaseHomeCompanyPickCommentCount = (companyPickId: string): Schema$CompanyPick => {
    let found
    _.forEach(this.homeCompanyPicks, pick => {
      if (pick.id === companyPickId) {
        pick.commentCount -= 1
        found = pick
      }
    })
    return found
  }

  @action
  decreaseCompanyPickCommentCount = (companyPickId: string): Schema$CompanyPick => {
    let found
    _.forEach(this.companyPicks, pick => {
      if (pick.id === companyPickId) {
        pick.commentCount -= 1
        found = pick
      }
    })
    this.setCompanyPicks(this.companyPicks)
    return found
  }

  @action
  decreaseNoticeCompanyPickCommentCount = (companyPickId: string): Schema$CompanyPick => {
    let found
    if (_.isEmpty(this.notice)) {
      return found
    }

    const { companyPicks } = this.notice
    _.forEach(companyPicks, pick => {
      if (pick.id === companyPickId) {
        pick.commentCount -= 1
        found = pick
      }
    })
    return found
  }

  @action
  decreaseManagerCommentCount = (managerUserPromiseId: string): void => {
    const found = _.find(this.companyPickManagers, manager => manager.userPromiseId === managerUserPromiseId)
    if (found) {
      found.commentCount -= 1
    }
  }

  updateManagerLikeCount = (managerUserPromiseId: string, isLike: boolean): void => {
    const found = _.find(this.companyPickManagers, manager => manager.userPromiseId === managerUserPromiseId)

    if (!found) {
      return
    }

    if (isLike) {
      found.likeCount += 1
    } else {
      found.likeCount -= 1
    }
  }

  updateHomeCompanyPicksLikeInfo = (companyPickId: string, isLike: boolean): void => {
    const foundHomePick = _.find(this.homeCompanyPicks, pick => pick.id === companyPickId)
    if (_.isNil(foundHomePick)) {
      return
    }

    if (isLike) {
      foundHomePick.likeCount += 1
    } else {
      foundHomePick.likeCount -= 1
    }
    foundHomePick.isLike = isLike
  }

  updateCompanyPicksLikeInfo = (companyPickId: string, isLike: boolean): void => {
    const foundPick = _.find(this.companyPicks, pick => pick.id === companyPickId)
    if (_.isNil(foundPick)) {
      return
    }

    if (isLike) {
      foundPick.likeCount += 1
    } else {
      foundPick.likeCount -= 1
    }
    foundPick.isLike = isLike
  }

  updateNoticeCompanyPicksLikeInfo = (companyPickId: string, isLike: boolean): void => {
    if (_.isEmpty(this.notice)) {
      return
    }

    const { companyPicks = [] } = this.notice
    const foundPick = _.find(companyPicks, pick => pick.id === companyPickId)
    if (_.isNil(foundPick)) {
      return
    }

    if (isLike) {
      foundPick.likeCount += 1
    } else {
      foundPick.likeCount -= 1
    }
    foundPick.isLike = isLike
  }

  updateSelectedCompanyPickLikeInfo = (isLike: boolean): void => {
    if (_.isNil(this.selectedCompanyPick)) {
      return
    }

    if (isLike) {
      this.selectedCompanyPick.likeCount += 1
    } else {
      this.selectedCompanyPick.likeCount -= 1
    }
    this.selectedCompanyPick.isLike = isLike
  }

  @action // like 는 토글이기 때문에 상태에 따라 갱신
  updateAllCompanyPicksLikeInfo = (companyPick: Schema$CompanyPick, isLike: boolean, isMatchupPageCall = false)
    : void => {
    const { id, userPromiseId } = companyPick
    this.updateManagerLikeCount(userPromiseId, isLike)

    const foundPick = _.find(this.companyPicks, pick => pick.id === id)
    const foundHomePick = _.find(this.homeCompanyPicks, pick => pick.id === id)
    if (_.isNil(foundPick) && _.isNil(foundHomePick)) {
      this.updateSelectedCompanyPickLikeInfo(isLike)
      if (isMatchupPageCall) {
        // 매치업 페이지를 통해 증가할때는 채팅창 프리미엄 분석가 픽 좋아요 상태가 갱신되어야 한다.
        this.updateNoticeCompanyPicksLikeInfo(id, isLike)
      }
      return
    }

    this.updateHomeCompanyPicksLikeInfo(id, isLike)  // 대상을 직접 갱신
    this.updateCompanyPicksLikeInfo(id, isLike)      // 대상을 복사 한다음 갱신
    this.updateNoticeCompanyPicksLikeInfo(id, isLike)  // 대상을 직접 갱신
  }

  updateHomeCompanyPicksFavoriteMatchInfo = (matchId: number, isFavoriteMatch: boolean): void => {
    _.forEach(this.homeCompanyPicks, pick => {
      if (pick.matchId === matchId) {
        pick.isFavoriteMatch = isFavoriteMatch
      }
    })
  }

  updateCompanyPicksFavoriteMatchInfo = (matchId: number, isFavoriteMatch: boolean): void => {
    const copiedCompanyPicks = _.cloneDeep(this.companyPicks)
    _.forEach(copiedCompanyPicks, pick => {
      if (pick.matchId === matchId) {
        pick.isFavoriteMatch = isFavoriteMatch
      }
    })
    this.setCompanyPicks(copiedCompanyPicks)
  }

  updateSelectedCompanyPickFavoriteMatchInfo = (matchId: number, isFavoriteMatch: boolean): void => {
    if (_.isNil(this.selectedCompanyPick)) {
      return
    }

    if (this.selectedCompanyPick.matchId === matchId) {
      this.selectedCompanyPick.isFavoriteMatch = isFavoriteMatch
    }
  }

  updateNoticeCompanyPicksFavoriteMatchInfo = (matchId: number, isFavoriteMatch: boolean): void => {
    if (_.isEmpty(this.notice)) {
      return
    }

    const { companyPicks = [] } = this.notice
    _.forEach(companyPicks, pick => {
      if (pick.matchId === matchId) {
        pick.isFavoriteMatch = isFavoriteMatch
      }
    })
  }

  @action
  updateAllCompanyPickCommentsLikeInfo = (comment: Schema$UserComment) => {
    const { id: companyPickCommentId, isLike } = comment
    this.updateSelectedCompanyPickRecentCommentsLikeInfo(companyPickCommentId, isLike)
  }

  updateSelectedCompanyPickRecentCommentsLikeInfo = (
    companyPickCommentId: string,
    isLike: boolean,
  ) => {
    const comment = _.find(this.selectedCompanyPickRecentComments, { id: companyPickCommentId })

    if (!comment) {
      return
    }

    if (isLike) {
      comment.likeCount += 1
    } else {
      comment.likeCount -= 1
    }

    comment.isLike = isLike
  }

  @action
  updateAllCompanyPicksFavoriteMatchInfo = (matchId: number, isFavoriteMatch: boolean): void => {
    this.updateHomeCompanyPicksFavoriteMatchInfo(matchId, isFavoriteMatch)
    this.updateCompanyPicksFavoriteMatchInfo(matchId, isFavoriteMatch)
    this.updateSelectedCompanyPickFavoriteMatchInfo(matchId, isFavoriteMatch)
    this.updateNoticeCompanyPicksFavoriteMatchInfo(matchId, isFavoriteMatch)
  }

  updateHomeCompanyPickAlreadyRead(companyPickId: string, isAlreadyRead: boolean): void {
    const foundHomePick = _.find(this.homeCompanyPicks, pick => pick.id === companyPickId)
    if (_.isNil(foundHomePick)) {
      return
    }
    foundHomePick.isAlreadyRead = isAlreadyRead
  }

  updateCompanyPickAlreadyRead(companyPickId: string, isAlreadyRead: boolean): void {
    const foundPick = _.find(this.companyPicks, pick => pick.id === companyPickId)
    if (_.isNil(foundPick)) {
      return
    }
    foundPick.isAlreadyRead = isAlreadyRead
  }

  updateSelectedCompanyPickAlreadyRead = (companyPickId: string, isAlreadyRead: boolean): void => {
    if (_.isNil(this.selectedCompanyPick)) {
      return
    }
    this.selectedCompanyPick.isAlreadyRead = isAlreadyRead
  }


  updateNoticeCompanyPickAlreadyRead = (companyPickId: string, isAlreadyRead: boolean): void => {
    if (_.isEmpty(this.notice)) {
      return
    }

    const { companyPicks = [] } = this.notice
    _.forEach(companyPicks, pick => {
      if (pick.id === companyPickId) {
        pick.isAlreadyRead = isAlreadyRead
      }
    })
  }

  @action
  updateAllCompanyPicksAlreadyReadInfo = (companyPickId: string, isAlreadyRead = true): void => {
    this.updateHomeCompanyPickAlreadyRead(companyPickId, isAlreadyRead)
    this.updateCompanyPickAlreadyRead(companyPickId, isAlreadyRead)
    this.updateSelectedCompanyPickAlreadyRead(companyPickId, isAlreadyRead)
    this.updateNoticeCompanyPickAlreadyRead(companyPickId, isAlreadyRead)
  }

  @action
  setNotice = (notice: Schema$CompanyPickNotice): void => {
    this.notice = notice
  }

  @action
  setIsMatchupPageCall = (isMatchupPageCall: boolean): void => {
    this.isMatchupPageCall = isMatchupPageCall
  }

  toggleCompanyPickLike = async (companyPick: Schema$CompanyPick, isMatchupPageCall = false): Promise<void> => {
    if (this.isCompanyPickLikeLoading) {
      return
    }

    try {
      this.isCompanyPickLikeLoading = true
      const {
        isLike,
        activityInfo,
      } = await this.api.companyPick.toggleCompanyPickLike({ companyPickId: companyPick.id })
      this.updateAllCompanyPicksLikeInfo(companyPick, isLike, isMatchupPageCall)
      if (isLike) {
        runInAction(() => {
          globalStore.addActivityScoreNoticeQueue(activityInfo)
        })
      }
    } catch (err) {
      console.error(err)
    } finally {
      this.isCompanyPickLikeLoading = false
    }
  }

  toggleFavoriteMatch = async (companyPick: Schema$CompanyPick): Promise<void> => {
    if (this.isFavoriteMatchLoading) {
      return
    }

    if (!authStore.isSignedIn) {
      return
    }

    const { matchId, sportId, dateOfMatch } = companyPick
    if (!Utils.isNotStartedMatch(dateOfMatch)) {
      return
    }

    try {
      this.isFavoriteMatchLoading = true

      const isFavoriteMatch = await matchupStore.toggleFavoriteMatch({ matchId, sportId, dateOfMatch })
      if (isFavoriteMatch) {
        matchupStore.increaseFavoriteTotalPreMatchCount()
      } else {
        matchupStore.decreaseFavoriteTotalPreMatchCount()
      }
      matchupStore.updateIsFavoritePreMatchInTournamentTracks(matchId, isFavoriteMatch)
      this.updateAllCompanyPicksFavoriteMatchInfo(matchId, isFavoriteMatch)
    } catch (err) {
      console.error(err)
    } finally {
      this.isFavoriteMatchLoading = false
    }
  }

  moveToFavoriteMatchList = () => {
    if (!authStore.isSignedIn) {
      return
    }
    matchupStore.setMatchTabInitializeSportId(SPORTS_TYPE.TODAY)
    // 화면 이동이 필요한 경우 MatchTab 이 초기화 되면서 다른 탭으로 초기화 될 수 있도록 한다.
    matchupStore.setMatchTabInitializeMajorUniqueId(EXCEPTIONAL_UNIQUE_ID.FAVORITE_MATCH)
    // matchupStore.setMatchTabInitializeSportId(EXCEPTIONAL_SPORT_ID_GROUP.FAVORITE)
    matchupStore.setIsNeedToFavoriteMatchIdsWhenFetchTournamentTracks(true)

    const clubId = Utils.getInitializeClubId()
    Utils.loadOrReload(`/matchup/cross/${clubId}`)
  }

  saveLastVisibleNoticeId() {
    if (_.isEmpty(this.notice)) {
      return
    }

    const id = _.trim(this.notice?.id)
    StorageUtils.saveLastVisibleCompanyPickNoticeId(id)
  }

  saveLastVisibleCompanyPickIdsInNotice() {
    if (_.isEmpty(this.notice)) {
      return
    }

    const ids = _.map(this.notice?.companyPicks, pick => pick.id)
    StorageUtils.saveLastVisibleCompanyPickNoticeCompanyPickIds(ids)
  }

  @action
  updateIsNewNotice() {
    if (_.isEmpty(this.notice)) {
      return
    }

    const currentNoticeId = this.notice?.id
    const lastVisibleNoticeId = _.trim(StorageUtils.getLastVisibleCompanyPickNoticeId())

    if (_.isEmpty(currentNoticeId)) {
      this.isNewNotice = false
      return
    }

    const currentCompanyPickIds = _.map(this.notice?.companyPicks, pick => pick.id)
    const lastVisibleCompanyPickIds = StorageUtils.getLastVisibleCompanyPickNoticeCompanyPickIds()

    let isAllExist = true
    _.forEach(currentCompanyPickIds, pickId => {
      if (!_.includes(lastVisibleCompanyPickIds, pickId)) {
        isAllExist = false
        return false
      }
      return true
    })

    if (currentNoticeId !== lastVisibleNoticeId || !isAllExist) {
      this.isNewNotice = true
      return
    }

    this.isNewNotice = false
  }
}


export default new CompanyPickStore()
